import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, CreditCard, TriangleAlert } from "lucide-react";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useHistory } from "react-router";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { SettingsPageLayout } from "components/settings/settings-page-layout";

import { PermissionsKeys } from "components/permissions/permission-keys";
import { usePermissions } from "components/permissions/usePermissions";
import { ReactQueryErrorWrapper } from "components/shared/react-query-error-wrapper";
import { User, useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { HubPlan, UpdateSubscription } from "data/models";
import { updateSubscription, validateCoupon } from "data/mutations-common";
import {
  fetchCredits,
  fetchOrganisation,
  fetchPaymentMethod,
  fetchPlans,
  fetchSubscription,
} from "data/queries-common";
import * as Routes from "routes";
import { resolveIdentityId } from "utils/identity";

import CreditUseModal from "../modals/credit-use-modal";
import FeatureComparisonModal from "../modals/feature-comparison-modal";

import { ScheduledCancellationAlert } from "../scheduled-cancellation-alert";
import { ScheduledPlanChangeAlert } from "../scheduled-plan-change-alert";

import { AvailablePlansSection } from "./available-plans-section";
import { Button } from "@/components/ui/button";
import {
  buildChangeSummary,
  getChangeType,
  getPlanByPriceLookupId,
  getSuccessText,
  isCouponValid,
} from "./change-summary";
import { ConfirmChangesCard } from "./confirm-changes-card";
import { CouponCard } from "./coupon-card";
import { OverageConfigCard } from "./overage-config-card";

export const ManageSubscriptionPage = () => {
  const history = useHistory();
  const [identityState] = useIdentity();
  const identityId = resolveIdentityId(identityState);
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const user = identityState as User;

  const [annualPlans, setAnnualPlans] = useState(false);
  const [selectedPrice, setSelectedPrice] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<HubPlan | null>(null);
  const [overageInput, setOverageInput] = useState(0);
  const [newOverageBudget, setNewOverageBudget] = useState(0);
  const [trialSelected, setTrialSelected] = useState(false);
  const [coupon, setCoupon] = useState("");
  const [featureComparisonOpen, setFeatureComparisonOpen] = useState(false);
  const [creditUseOpen, setCreditUseOpen] = useState(false);

  const plansQuery = useQuery({
    queryKey: [QueryKeys.plans],
    queryFn: () => fetchPlans(),
  });
  const subscriptionQuery = useQuery({
    queryKey: [QueryKeys.subscription, identityId],
    queryFn: () => fetchSubscription(),
  });
  const paymentMethodQuery = useQuery({
    queryKey: [QueryKeys.paymentMethod, identityId],
    queryFn: () => fetchPaymentMethod(),
  });
  const creditsQuery = useQuery({
    queryKey: [QueryKeys.overage, identityId],
    queryFn: () => fetchCredits(),
  });
  const organisationQuery = useQuery({
    queryKey: [QueryKeys.organisation, identityId],
    queryFn: () => fetchOrganisation(),
  });
  const membershipsQuery = useQuery({
    queryKey: [identityState, QueryKeys.userMembership],
  });
  const orgMembershipsQuery = useQuery({
    queryKey: [QueryKeys.userMembership, user.cognitoSub],
  });

  useEffect(() => {
    if (creditsQuery.data?.overageDollarsLimit) {
      setOverageInput(creditsQuery.data.overageDollarsLimit);
    }
  }, [creditsQuery.isSuccess]);

  const currentPlan = subscriptionQuery.data?.activeSubscription?.plan ?? null;

  const onUserInitiatedTrial =
    subscriptionQuery.data?.activeSubscription?.trialType === "user";
  const onAdminInitiatedTrial =
    subscriptionQuery.data?.activeSubscription?.trialType === "admin";
  const onTrial = !!(onUserInitiatedTrial || onAdminInitiatedTrial);

  const hasScheduledCancellation = !!(
    subscriptionQuery.data?.activeSubscription?.cancelAtPeriodEnd === true
  );
  const hasScheduledDowngrade = !!(
    subscriptionQuery.data?.activeSubscription?.nextSub != null
  );

  const newPlanPermissionKeys = selectedPlan
    ? trialSelected
      ? [selectedPlan.product_key, "free_trial"]
      : [selectedPlan.product_key]
    : undefined;

  const { isPermitted: newPlanIsPermitted } = usePermissions({
    permissionSets: newPlanPermissionKeys,
  });

  const couponMutation = useMutation({
    mutationFn: (promoCode: string) => validateCoupon(promoCode),
  });

  const subscriptionMutation = useMutation({
    mutationFn: (values: UpdateSubscription) => updateSubscription(values),
    onSuccess: (updatedSubscription) => {
      queryClient.setQueryData([QueryKeys.subscription, identityId], {
        ...subscriptionQuery.data,
        activeSubscription: updatedSubscription.activeSubscription,
      });
      membershipsQuery.refetch();
      orgMembershipsQuery.refetch();
      enqueueSnackbar(
        getSuccessText(
          plansQuery.data?.availablePlans.find(
            (x) => x.price_lookup_id === selectedPrice,
          ) ?? null,
          currentPlan,
        ),
        { variant: "success" },
      );
      history.push(Routes.developerDashboard);
    },
  });

  const changeType = getChangeType(selectedPlan, currentPlan, onTrial);

  const couponValid = isCouponValid(
    selectedPlan?.price_lookup_id ?? "",
    couponMutation.data,
  );

  const canConfigureOverage = newPlanIsPermitted([
    PermissionsKeys.overageUpdate,
  ]);

  const blockedForPaymentMethodNeeded = !(
    paymentMethodQuery.data?.active || selectedPlan?.free_plan
  );

  const changeTypeAllowed = !!(
    selectedPlan &&
    (selectedPlan.tier !== currentPlan?.tier || onTrial)
  );

  const canContinue = !!(
    changeTypeAllowed &&
    !blockedForPaymentMethodNeeded &&
    (!hasScheduledCancellation || onAdminInitiatedTrial) &&
    !hasScheduledDowngrade
  );

  const canDoTrial =
    organisationQuery.isSuccess && !organisationQuery.data.hasUsedTrial;

  const handleCancel = () => {
    setSelectedPrice(null);
    setSelectedPlan(null);
    setCoupon("");
    setOverageInput(creditsQuery.data?.overageDollarsLimit ?? overageInput);
    setNewOverageBudget(0);
    couponMutation.reset();
  };

  const handlePlanSelect = (priceId: string, type: "selectable" | "trial") => {
    setSelectedPrice(priceId);
    if (plansQuery.data) {
      setSelectedPlan(
        getPlanByPriceLookupId(priceId, plansQuery.data.availablePlans),
      );
    }
    setTrialSelected(type === "trial");
  };

  const changes = buildChangeSummary({
    selectedPlan,
    currentRenewalDate: subscriptionQuery.data?.activeSubscription?.renewalDate,
    changeType,
    trialSelected,
    newOverageBudget,
    overageDollarsSpent: creditsQuery.data?.overageDollarsSpent ?? 0,
    coupon: couponMutation.data,
  });

  const active = subscriptionQuery.data?.activeSubscription;

  return (
    <SettingsPageLayout
      pageTitle={{
        icon: CreditCard,
        title: "Manage Subscription",
        description: "Select a plan and confirm your subscription changes.",
      }}
    >
      <div className="mx-auto flex flex-col gap-4">
        <div className="flex justify-end">
          <Button
            variant="default"
            onClick={() => history.push(Routes.orgSubscription)}
          >
            <ArrowLeft />
            Back to Subscription
          </Button>
        </div>

        <ReactQueryErrorWrapper
          queries={[
            subscriptionQuery,
            paymentMethodQuery,
            plansQuery,
            creditsQuery,
          ]}
          mutations={[subscriptionMutation, couponMutation]}
        />

        {subscriptionQuery.isSuccess &&
          active &&
          hasScheduledCancellation &&
          !onAdminInitiatedTrial && (
            <ScheduledCancellationAlert subscription={subscriptionQuery.data} />
          )}

        {subscriptionQuery.isSuccess && active && hasScheduledDowngrade && (
          <ScheduledPlanChangeAlert subscription={subscriptionQuery.data} />
        )}

        {plansQuery.isSuccess && subscriptionQuery.isSuccess && (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
            <div className="flex flex-col gap-6 min-w-0">
              <AvailablePlansSection
                planInfo={plansQuery.data}
                subscription={subscriptionQuery.data}
                annualPlans={annualPlans}
                setAnnualPlans={(val) => {
                  setAnnualPlans(val);
                  handleCancel();
                }}
                selectedPrice={selectedPrice}
                onSelect={handlePlanSelect}
                onOpenFeatureComparison={() => setFeatureComparisonOpen(true)}
                onOpenFeatureCosts={() => setCreditUseOpen(true)}
                canDoTrial={canDoTrial}
                onTrial={onTrial}
                currentPlan={currentPlan}
              />

              {selectedPlan && (
                <>
                  <OverageConfigCard
                    selectedPlan={selectedPlan}
                    canConfigureOverage={canConfigureOverage}
                    overageInput={overageInput}
                    onChange={(value, rounded) => {
                      setOverageInput(value);
                      setNewOverageBudget(rounded);
                    }}
                  />

                  <CouponCard
                    coupon={coupon}
                    setCoupon={setCoupon}
                    couponMutation={couponMutation}
                    selectedPlan={selectedPlan}
                    changeType={changeType}
                  />
                </>
              )}
            </div>

            <div className="lg:sticky lg:top-4 lg:self-start">
              <ConfirmChangesCard
                changes={changes}
                selectedPlan={selectedPlan}
                isLoading={subscriptionMutation.isPending}
                canSubmit={canContinue}
                canCancel={!!selectedPlan && !subscriptionMutation.isPending}
                trialSelected={trialSelected}
                onCancel={handleCancel}
                onSubmit={() => {
                  if (!selectedPrice) {
                    throw Error(
                      "Submitted subscription form without selecting a price!",
                    );
                  }
                  subscriptionMutation.mutate({
                    priceLookupId: selectedPrice,
                    overageLimitDollars: canConfigureOverage
                      ? newOverageBudget
                      : 0,
                    coupon: couponValid ? couponMutation.data?.id : undefined,
                    trial: trialSelected,
                  });
                }}
                newPlanIsPermitted={newPlanIsPermitted}
              />
            </div>
          </div>
        )}

        {!plansQuery.isSuccess && plansQuery.isError && (
          <Alert variant="destructive">
            <TriangleAlert />
            <AlertDescription>
              An error occurred while fetching plans.
            </AlertDescription>
          </Alert>
        )}

        <FeatureComparisonModal
          open={featureComparisonOpen}
          onClose={() => setFeatureComparisonOpen(false)}
        />
        <CreditUseModal
          open={creditUseOpen}
          onClose={() => setCreditUseOpen(false)}
        />
      </div>
    </SettingsPageLayout>
  );
};
